import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = '/api/admin';

const InfoCardsManager = ({ auth }) => {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingCard, setEditingCard] = useState(null); // null = создание
    const [formData, setFormData] = useState({
        key: '',
        title: '',
        content: '',
        sort_order: 0,
        is_enabled: true
    });

    // Загрузка списка карточек
    const loadCards = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/info-cards`, { auth });
            setCards(res.data);
        } catch (err) {
            console.error('Ошибка загрузки карточек', err);
            alert('Не удалось загрузить карточки');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCards();
    }, []);

    // Открыть модалку на создание
    const openCreateModal = () => {
        setEditingCard(null);
        setFormData({
            key: '',
            title: '',
            content: '',
            sort_order: cards.length,
            is_enabled: true
        });
        setModalOpen(true);
    };

    // Открыть модалку на редактирование
    const openEditModal = (card) => {
        setEditingCard(card);
        setFormData({
            key: card.key,
            title: card.title,
            content: card.content || '',
            sort_order: card.sort_order,
            is_enabled: card.is_enabled
        });
        setModalOpen(true);
    };

    const closeModal = () => setModalOpen(false);

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Сохранить (создать или обновить)
    const handleSubmit = async () => {
        try {
            if (editingCard) {
                // Обновление
                await axios.patch(`${API_BASE}/info-cards/${editingCard.id}`, formData, { auth });
            } else {
                // Создание
                await axios.post(`${API_BASE}/info-cards`, formData, { auth });
            }
            closeModal();
            loadCards();
        } catch (err) {
            alert(err.response?.data?.detail || 'Ошибка сохранения карточки');
        }
    };

    // Удалить карточку
    const handleDelete = async (id) => {
        if (!window.confirm('Удалить карточку?')) return;
        try {
            await axios.delete(`${API_BASE}/info-cards/${id}`, { auth });
            loadCards();
        } catch (err) {
            alert('Ошибка удаления');
        }
    };

    // Переключить видимость
    const handleToggleEnabled = async (card) => {
        try {
            await axios.patch(`${API_BASE}/info-cards/${card.id}`, { is_enabled: !card.is_enabled }, { auth });
            loadCards();
        } catch (err) {
            alert('Ошибка изменения статуса');
        }
    };

    // Переместить карточку вверх/вниз (меняем sort_order местами)
    const moveCard = async (index, direction) => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === cards.length - 1) return;

        const sortedCards = [...cards].sort((a, b) => a.sort_order - b.sort_order);
        const currentCard = sortedCards[index];
        const neighborCard = sortedCards[direction === 'up' ? index - 1 : index + 1];

        // Меняем sort_order местами
        const currentSort = currentCard.sort_order;
        const neighborSort = neighborCard.sort_order;

        try {
            await axios.patch(`${API_BASE}/info-cards/${currentCard.id}`, { sort_order: neighborSort }, { auth });
            await axios.patch(`${API_BASE}/info-cards/${neighborCard.id}`, { sort_order: currentSort }, { auth });
            loadCards();
        } catch (err) {
            alert('Ошибка изменения порядка');
        }
    };

    if (loading) return <div className="text-center py-4">Загрузка карточек...</div>;

    // Сортируем для отображения
    const sortedCards = [...cards].sort((a, b) => a.sort_order - b.sort_order);

    return (
        <div className="bg-white p-6 rounded-lg shadow mb-8">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl">Инфо-карточки (правила площадки)</h2>
                <button
                    onClick={openCreateModal}
                    className="bg-gold-accent text-white px-4 py-2 rounded hover:bg-yellow-600"
                >
                    + Добавить карточку
                </button>
            </div>

            {sortedCards.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Нет карточек. Создайте первую.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border">
                        <thead>
                            <tr className="bg-gray-100 border-b">
                                <th className="text-left p-3">Ключ</th>
                                <th className="text-left p-3">Заголовок</th>
                                <th className="text-left p-3">Содержание</th>
                                <th className="text-left p-3">Порядок</th>
                                <th className="text-left p-3">Активна</th>
                                <th className="text-left p-3">Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedCards.map((card, idx) => (
                                <tr key={card.id} className="border-b hover:bg-gray-50">
                                    <td className="p-3">{card.key}</td>
                                    <td className="p-3">{card.title}</td>
                                    <td className="p-3 max-w-md truncate">{card.content}</td>
                                    <td className="p-3">{card.sort_order}</td>
                                    <td className="p-3">
                                        <button
                                            onClick={() => handleToggleEnabled(card)}
                                            className={`px-2 py-1 rounded text-sm ${card.is_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                                        >
                                            {card.is_enabled ? 'Вкл' : 'Выкл'}
                                        </button>
                                    </td>
                                    <td className="p-3 flex gap-2">
                                        <button onClick={() => openEditModal(card)} className="text-blue-600" title="Редактировать">✏️</button>
                                        <button onClick={() => handleDelete(card.id)} className="text-red-600" title="Удалить">🗑️</button>
                                        {idx > 0 && (
                                            <button onClick={() => moveCard(idx, 'up')} className="text-gray-600" title="Вверх">⬆️</button>
                                        )}
                                        {idx < sortedCards.length - 1 && (
                                            <button onClick={() => moveCard(idx, 'down')} className="text-gray-600" title="Вниз">⬇️</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Модальное окно создания/редактирования */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <h3 className="text-xl font-bold mb-4">{editingCard ? 'Редактировать карточку' : 'Новая карточка'}</h3>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-gray-700 mb-1">Ключ (уникальный, только латиница/цифры/подчёркивание)</label>
                                    <input
                                        type="text"
                                        name="key"
                                        value={formData.key}
                                        onChange={handleFormChange}
                                        disabled={!!editingCard}
                                        className="w-full p-2 border rounded disabled:bg-gray-100"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 mb-1">Заголовок</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleFormChange}
                                        className="w-full p-2 border rounded"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 mb-1">Содержание (HTML разрешён, можно использовать переносы строк)</label>
                                    <textarea
                                        name="content"
                                        value={formData.content}
                                        onChange={handleFormChange}
                                        rows="4"
                                        className="w-full p-2 border rounded"
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 mb-1">Порядок (меньше – выше)</label>
                                    <input
                                        type="number"
                                        name="sort_order"
                                        value={formData.sort_order}
                                        onChange={handleFormChange}
                                        className="w-32 p-2 border rounded"
                                    />
                                </div>
                                <div>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            name="is_enabled"
                                            checked={formData.is_enabled}
                                            onChange={handleFormChange}
                                        />
                                        <span>Активна (показывать гостям)</span>
                                    </label>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <button onClick={closeModal} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Отмена</button>
                                <button onClick={handleSubmit} className="px-4 py-2 bg-gold-accent text-white rounded hover:bg-yellow-600">
                                    {editingCard ? 'Сохранить' : 'Создать'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InfoCardsManager;